import { useCallback, useLayoutEffect, useRef, useState } from 'react';
import type { PipelineStageId } from './PipelineStrip.transform';

type MarkerPosition = { x: number; y: number; stage: PipelineStageId; animate: boolean };
type MarkerRead = {
  caseId: string;
  label: string;
  stage: PipelineStageId;
  offset?: number;
  /** 0-based lane inside the stage cell; cases sharing a cell stack by this. */
  stack?: number;
};

/** Space between stacked case tokens. */
const STACK_GAP = 2;
/** Keeps a token off the cell's own border. */
const CELL_INSET = 3;

/** One stable case token, measured against the strip cells and moved by transform. */
export function CaseMarker({ marker }: { marker: MarkerRead | null }) {
  const layerRef = useRef<HTMLDivElement>(null);
  const chipRef = useRef<HTMLSpanElement>(null);
  const previousStage = useRef<PipelineStageId | null>(null);
  const moving = useRef(false);
  const settleTimer = useRef<number | null>(null);
  const [position, setPosition] = useState<MarkerPosition | null>(null);
  const [chip, setChip] = useState<{ w: number; h: number } | null>(null);

  const measureChip = useCallback(() => {
    const node = chipRef.current;
    if (!node) return;
    const box = node.getBoundingClientRect();
    if (!box.width) return;
    setChip((current) => (current && Math.abs(current.w - box.width) < 0.5 && Math.abs(current.h - box.height) < 0.5
      ? current
      : { w: box.width, h: box.height }));
  }, []);

  // The token's own width decides whether it can sit centred in its cell; measure
  // it once mounted so a wide label clamps inside the cell instead of straddling.
  useLayoutEffect(() => {
    const node = chipRef.current;
    if (!node) return undefined;
    measureChip();
    const observer = new ResizeObserver(measureChip);
    observer.observe(node);
    return () => observer.disconnect();
  }, [measureChip, marker?.caseId, position?.stage]);

  useLayoutEffect(() => {
    if (!marker) {
      previousStage.current = null;
      moving.current = false;
      setPosition(null);
      setChip(null);
      if (settleTimer.current !== null) {
        window.clearTimeout(settleTimer.current);
        settleTimer.current = null;
      }
      return undefined;
    }
    const layer = layerRef.current;
    const root = layer?.parentElement;
    const stageNode = root?.querySelector<HTMLElement>(`[data-stage-id="${marker.stage}"]`);
    const laneNode = root?.querySelector<HTMLElement>('[data-marker-lane]');
    if (!layer || !root || !stageNode || !laneNode) return undefined;

    const measure = () => {
      const layerBox = layer.getBoundingClientRect();
      const stageBox = stageNode.getBoundingClientRect();
      const laneBox = laneNode.getBoundingClientRect();
      const changed = previousStage.current !== null && previousStage.current !== marker.stage;
      if (changed) {
        moving.current = true;
        if (settleTimer.current !== null) window.clearTimeout(settleTimer.current);
        settleTimer.current = window.setTimeout(() => {
          moving.current = false;
          setPosition((current) => current ? { ...current, animate: false } : current);
        }, 650);
      }
      previousStage.current = marker.stage;
      const stageLeft = stageBox.left - layerBox.left;
      const halfWidth = (chip?.w ?? 0) / 2 + CELL_INSET;
      const centred = stageLeft + stageBox.width / 2 + (marker.offset ?? 0);
      const minX = stageLeft + halfWidth;
      const maxX = stageLeft + stageBox.width - halfWidth;
      const x = maxX >= minX ? Math.min(Math.max(centred, minX), maxX) : centred;
      const laneTop = laneBox.top - layerBox.top + laneBox.height / 2;
      setPosition({
        x,
        y: laneTop + (marker.stack ?? 0) * ((chip?.h ?? 18) + STACK_GAP),
        stage: marker.stage,
        animate: moving.current,
      });
    };

    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(root);
    observer.observe(laneNode);
    observer.observe(stageNode);
    window.addEventListener('resize', measure);
    return () => {
      observer.disconnect();
      window.removeEventListener('resize', measure);
      if (settleTimer.current !== null) {
        window.clearTimeout(settleTimer.current);
        settleTimer.current = null;
      }
    };
  }, [marker?.caseId, marker?.stage, marker?.offset, marker?.stack, chip]);

  return (
    <div ref={layerRef} aria-hidden="true" className="pointer-events-none absolute inset-0 z-20">
      {marker && position ? (
        <span
          key={marker.caseId}
          ref={chipRef}
          data-case-marker={marker.caseId}
          data-case-stage={position.stage}
          data-marker-stack={marker.stack ?? 0}
          data-marker-animate={position.animate || undefined}
          className={`absolute left-0 top-0 inline-flex max-w-[calc(100%-0.5rem)] items-center gap-1 rounded-full border border-[var(--color-accent-500)] bg-[var(--surface-2)] px-2 py-[3px] text-[10px] font-semibold leading-none tracking-[0.02em] text-[var(--color-accent-600)] shadow-sm dark:text-[var(--color-accent-300)] ${
            position.animate
              ? 'transition-transform duration-[600ms] ease-[cubic-bezier(0.22,1,0.36,1)] motion-reduce:transition-none'
              : 'transition-none'
          }`}
          style={{ transform: `translate3d(${position.x}px, ${position.y}px, 0) translate(-50%, -50%)` }}
        >
          <span className="size-1.5 shrink-0 rounded-full bg-[var(--color-accent-500)]" />
          <span className="truncate">{marker.label}</span>
        </span>
      ) : null}
    </div>
  );
}
